import db from "../../database";
import { getOAuthHeaders } from "../../constants/oAuthValidation";
import { v4 as uuidv4 } from "uuid";
import { trackSync } from "../../utils/healthReporter";
import { verifySyncCompleteness } from "../../utils/dataIntegrity";
import { getCountries, getFinancialAccounts, getStates, syncDocTypes, syncRFIDData, syncRFIDScanConfig, syncSalesRepData } from "../../services/generic";
import {
  syncAllProductCategories,
  syncProductCategories,
  syncPosSaleTypes,
  syncProductsUom,
  syncProducts,
  syncPricingRules,
  syncOfflineDta,
  syncPosConfigData,
  syncLoyalityData,
  syncGiftCardData,
  getApprovals,
  getExpenseCategory,
  getCompanionRules,
  getRevenueCategories,
  getCustomerConfig,
  syncRestaurantTables,
  getFBOrdersData,
  getLoyaltyInfo,
} from "../../services";
import dayjs from "dayjs";
import useSyncProgressStore from "../../stores/useSyncProgressStore";
import useAuthStore from "../../stores/useAuthStore";
import useReferenceStore from "../../stores/useReferenceStore";
import useSessionStore from "../../stores/useSessionStore";

let cleanToken;
let syncId;
let tillId;
const authHeaders = getOAuthHeaders();
if (authHeaders) {
  cleanToken = authHeaders.access_token;
}

export const processSync = async (data, type) => {
  const setProgress = useSyncProgressStore.getState().setProgress;
  const tillData = data ? data : useAuthStore.getState().tillData;
  let syncType = "Incremental Sync";
  const lastUpdatedTime = useSessionStore.getState().lastUpdatedTime;
  if (lastUpdatedTime === undefined || lastUpdatedTime === null) {
    syncType = "Full Sync";
    await deletePreviousData();
  }
  try {
    let startTime = dayjs(new Date()).format("YYYY-MM-DD HH:mm:ss");
    tillId = useAuthStore.getState().tillValue?.cwr_till_id;
    syncId = uuidv4().replace(/-/g, "").toUpperCase();
    await db.dataSyncSummary.add({ syncId: syncId, syncType: syncType, syncStartTime: startTime, syncStatus: "IN_PROGRESS" }); // DI-15: Track sync status
    setProgress({ percent: "15%" });
    setProgress({ productCategoryPercentage: "38" });
    await syncAllProductCategories(tillId, syncId);
    setProgress({ percent: "30%" });
    setProgress({ productCategoryPercentage: "55" });
    setProgress({ productPercentage: "15" });
    await syncProductCategories(tillData, tillId, syncId);
    setProgress({ percent: "45%" });
    setProgress({ productCategoryPercentage: "70" });
    setProgress({ productPercentage: "30" });
    await syncPosSaleTypes(syncId, tillId, tillData);
    setProgress({ percent: "53%" });
    setProgress({ productCategoryPercentage: "100" });
    setProgress({ productPercentage: "47" });
    await syncProductsUom(syncId, tillId);
    setProgress({ percent: "64%" });
    setProgress({ productPercentage: "56" });
    setProgress({ percent: "75%" });
    setProgress({ productPercentage: "69" });
    await syncProducts(tillData, syncId, tillId);
    setProgress({ percent: "84%" });
    setProgress({ promotionPercentage: "25" });
    setProgress({ productPercentage: "77" });
    await syncPricingRules(tillData, syncId, tillId);
    await syncOfflineDta(tillData, syncId, tillId);
    setProgress({ percent: "90%" });
    setProgress({ promotionPercentage: "55" });
    setProgress({ productPercentage: "85" });
    await syncPosConfigData(syncId, tillId);
    setProgress({ percent: "95%" });
    setProgress({ productPercentage: "100" });
    setProgress({ promotionPercentage: "79" });
    // Non-critical sync calls — each wrapped individually so one failure
    // doesn't block login. POS works without these; they'll sync next time.
    const nonCritical = async (name, fn) => {
      try {
        await fn();
      } catch (e) {
        console.warn(`[Sync] ${name} failed (non-blocking):`, e?.message);
      }
    };
    await nonCritical("LoyaltyData", () => syncLoyalityData(syncId, tillId));
    await nonCritical("GiftCardData", () => syncGiftCardData(syncId, tillId));
    await nonCritical("Approvals", () => getApprovals());
    await nonCritical("ExpenseCategory", () => getExpenseCategory(syncId));
    await nonCritical("RevenueCategories", () => getRevenueCategories(tillData.tillAccess.csBunit.csBunitId));
    nonCritical("CompanionRules", () => getCompanionRules());
    const bunitId = tillData.tillAccess.csBunit.csBunitId;
    const value = { bunitId };
    const newStringifiedFields = JSON.stringify(value).replace(/\\"/g, '\\"');
    const newStringRequest = JSON.stringify(newStringifiedFields);
    const apiBuilder = {
      query: `mutation { executeAPIBuilder(apiBuilderId:"69aabb8c6c3dd15f04fe382d", params: ${newStringRequest})} `,
    };
    await nonCritical("FinancialAccounts", () => getFinancialAccounts(apiBuilder));
    await nonCritical("CustomerConfig", () => getCustomerConfig());
    await nonCritical("DocTypes", () => syncDocTypes());
    nonCritical("RestaurantTables", () => syncRestaurantTables());
    await nonCritical("RFIDData", () => syncRFIDData(syncId));
    await nonCritical("RFIDScanConfig", () => syncRFIDScanConfig(tillData));
    await nonCritical("SalesRepData", () => syncSalesRepData(tillData));
    await nonCritical("FilterBrandCategory", () => filterBrandAndCategoty());
    await nonCritical("FBOrdersData", () => getFBOrdersData());
    await nonCritical("States", () => getStates());
    await nonCritical("Countries", () => getCountries());
    const customerConfig = useReferenceStore.getState().customerConfigs;
    const customerConfigData = customerConfig ? JSON.parse(customerConfig[0]?.configuration) : {};
    const retailCustomerApiDataSources = customerConfigData?.apiDataSources || [];
    const loyaltyApiConfig = retailCustomerApiDataSources?.find((api) => api.name === "getLoyaltyData");
    if (loyaltyApiConfig && loyaltyApiConfig?.endpoint) {
      const params = {
        startRow: 0,
        endRow: 10,
      };
      try {
        await getLoyaltyInfo({
          endpoint: loyaltyApiConfig?.endpoint,
          params,
        });
      } catch (loyaltyErr) {
        console.warn("Loyalty info fetch failed (non-blocking):", loyaltyErr?.message);
      }
    }
    setProgress({ percent: "99%" });
    setProgress({ promotionPercentage: "100" });
    await db.dataSyncSummary
      .where("syncId")
      .equals(syncId)
      .modify((event) => {
        event.syncEndTime = dayjs(new Date()).format("YYYY-MM-DD HH:mm:ss");
        event.syncStatus = "COMPLETED"; // DI-15: Mark sync as completed
      });
    useSessionStore.getState().setLastUpdatedTime(startTime);
    trackSync(true);

    // DI-15 / DI-19: Verify sync completeness — warn if critical tables are empty
    try {
      const syncVerification = await verifySyncCompleteness(db);
      if (!syncVerification.complete) {
        console.warn("[DI-19] Sync completed but data verification failed:", syncVerification.warnings);
        // Store the verification result so the app can show a warning
        await db.dataSyncSummary
          .where("syncId")
          .equals(syncId)
          .modify((event) => {
            event.verificationWarnings = syncVerification.warnings;
            event.syncStatus = "INCOMPLETE";
          });
      } else {
        console.info("[DI-19] Sync verification passed:", JSON.stringify(syncVerification.counts));
      }
    } catch (verifyErr) {
      console.warn("[DI-19] Sync verification check failed (non-blocking):", verifyErr?.message);
    }

    // Push context to background scheduler (Electron only)
    if (window.electronAPI?.scheduler) {
      const tillDataLocal = useAuthStore.getState().tillData || {};
      const tillValueLocal = useAuthStore.getState().tillValue || {};
      const cookies = document.cookie.split("; ");
      let accessToken = "";
      for (const c of cookies) {
        const [name, ...rest] = c.split("=");
        if (name.includes("accessToken")) accessToken = rest.join("=");
      }
      const ctx = {
        bUnitId: tillDataLocal?.tillAccess?.csBunit?.csBunitId || "",
        tillId: tillValueLocal?.cwr_till_id || "",
        accessToken,
        graphqlUrl: window.__CW_TENANT_URLS__?.REACT_APP_serverUrl || "",
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      };
      for (const [k, v] of Object.entries(ctx)) {
        window.electronAPI.scheduler.setContext(k, v);
      }
    }
  } catch (error) {
    console.debug(error);
    trackSync(false);
    // DI-15: Mark sync as failed so the app can detect partial data on next startup
    try {
      if (syncId) {
        await db.dataSyncSummary
          .where("syncId")
          .equals(syncId)
          .modify((event) => {
            event.syncStatus = "FAILED";
            event.syncError = error?.message || String(error);
          });
      }
    } catch (markErr) {
      console.warn("[DI-15] Failed to mark sync as FAILED:", markErr?.message);
    }
    throw error;
  }
};

export const deletePreviousData = () => {
  return new Promise(async (deletionSuccess, deletionFailure) => {
    try {
      await db.productCategories.clear();
      await db.AllProductCategories.clear();
      await db.products.clear();
      await db.pricingRules.clear();
      await db.posSaletypes.clear();
      await db.productUom.clear();
      await db.rfidData.clear();
      await db.loyalityData.clear();
      await db.POSWorkFlowRules.clear();
      await db.approvers.clear();
      await db.productBrands.clear();
      await db.giftCardData.clear();
      await db.docTypesData.clear();
      useSessionStore.getState().setLastUpdatedTime(null);
      deletionSuccess();
    } catch (error) {
      deletionFailure(error);
    }
  });
};

const filterBrandAndCategoty = async () => {
  // Use indexed query on isactive (stored as integer 1 in SQLite) instead of JS filter over all 35K products
  let products = await db.products.where("isactive").equals(true).toArray();
  let brandDetails = await db.productBrands.toArray();
  let categoryDetails = await db.productCategories.toArray();
  // Fall back to AllProductCategories if productCategories is empty (some tenants' productCategory API returns no data)
  if (categoryDetails.length === 0) {
    categoryDetails = await db.AllProductCategories.toArray();
  }
  await db.productBrands.clear();
  await db.productCategories.clear();
  const getUniqueBrandsAndCategories = (products) => {
    const brands = new Set();
    const categories = new Set();

    products.forEach((product) => {
      brands.add(product.brandId);
      categories.add(product.mProductCategoryId);
    });

    return {
      brands: Array.from(brands),
      categories: Array.from(categories),
    };
  };
  const { brands, categories } = getUniqueBrandsAndCategories(products);

  const filterBrandsAndCategories = (brands, categories, brandDetails, categoryDetails) => {
    const filteredBrands = brands.reduce((acc, brandId) => {
      const brandDetail = brandDetails.find((brand) => brand.brandId === brandId);
      if (brandDetail) {
        acc.push(brandDetail);
      }
      return acc;
    }, []);

    const filteredCategories = categories.reduce((acc, categoryId) => {
      const categoryDetail = categoryDetails.find((category) => category.mProductCategoryId === categoryId);
      if (categoryDetail) {
        acc.push(categoryDetail);
      }
      return acc;
    }, []);

    return {
      filteredBrands,
      filteredCategories,
    };
  };

  const { filteredBrands, filteredCategories } = filterBrandsAndCategories(brands, categories, brandDetails, categoryDetails);
  await db.productBrands.bulkPut(filteredBrands);
  await db.productCategories.bulkPut(filteredCategories);
};
